import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form, FieldArray } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import Dropzone from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  createPerformer,
  updatePerformer,
  uploadPerformerHeadshot,
  fetchPerformers,
  sendPerformerEditLinkEmail,
} from "../../../../../redux/features/performersSlice";
import {
  LINK_PRESETS,
  linkToPresetValue,
  presetValueToLink,
} from "./linkPresets";
import HeadshotCropModal from "./HeadshotCropModal";
import LoadingOverlay from "../../../../LoadingOverlay";

const isValidUrl = (v) => !v || /^https?:\/\/.+/i.test((v || "").trim());

export const validationSchema = yup.object({
  name: yup.string().trim().required("Name is required"),
  email: yup
    .string()
    .trim()
    .when("sendEditLinkAfterCreate", {
      is: true,
      then: (schema) => schema.required("Email is required to send the edit link."),
      otherwise: (schema) => schema,
    })
    .test("email-format", "Invalid email", (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)),
  bio: yup.string().trim(),
  credits: yup.string().trim(),
  sendEditLinkAfterCreate: yup.boolean(),
  links: yup.array().of(
    yup.object({
      presetId: yup.string(),
      value: yup.string().trim(),
      customLabel: yup.string().trim(),
    }).test("link-value", function (link) {
      if (!link?.presetId) return true;
      const preset = LINK_PRESETS.find((p) => p.id === link.presetId);
      if (!preset) return true;
      const val = (link.value || "").trim();
      if (preset.inputType === "username") {
        if (!val) return this.createError({ message: "Enter a username" });
        return true;
      }
      if (preset.inputType === "url" || preset.inputType === "other") {
        if (!val) return this.createError({ message: "Enter a URL" });
        if (!isValidUrl(val)) return this.createError({ message: "Enter a valid URL (e.g. https://...)" });
        return true;
      }
      return true;
    }),
  ),
});

const PerformerForm = ({
  performer,
  organizationId,
  onSuccess,
  onCancel,
  submitLabel,
  cancelLabel = "Cancel",
  onMessage,
}) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(performer?.id);
  const [headshotFile, setHeadshotFile] = useState(null);
  const [headshotPreviewUrl, setHeadshotPreviewUrl] = useState(null);
  const [clearHeadshot, setClearHeadshot] = useState(false);
  const [pendingHeadshotFile, setPendingHeadshotFile] = useState(null);

  React.useEffect(() => {
    if (!headshotFile) {
      setHeadshotPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(headshotFile);
    setHeadshotPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [headshotFile]);

  const initialValues = {
    name: performer?.name ?? "",
    email: performer?.email ?? "",
    bio: performer?.bio ?? "",
    credits: performer?.credits ?? "",
    sendEditLinkAfterCreate: false,
    links:
      performer?.links?.length > 0
        ? performer.links.map((l) => {
            const p = linkToPresetValue(l);
            return { presetId: p.presetId, value: p.value, customLabel: p.customLabel ?? "" };
          })
        : [{ presetId: "", value: "", customLabel: "" }],
  };

  const defaultSubmitLabel = isEdit ? "Save" : "Add performer";
  const resolvedSubmitLabel = submitLabel ?? defaultSubmitLabel;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (values, { setFieldError }) => {
        const links = (values.links || [])
          .filter((l) => l.presetId && (l.value || "").trim())
          .map((l) => presetValueToLink(l.presetId, l.value, l.customLabel))
          .filter(Boolean)
          .map((l) => ({ label: (l.label || "").trim() || "Link", url: l.url.trim() }));
        const payload = {
          name: values.name.trim(),
          email: (values.email || "").trim(),
          bio: (values.bio || "").trim(),
          credits: (values.credits || "").trim(),
          links,
        };
        if (isEdit && clearHeadshot) {
          payload.headshotUrl = null;
          payload.headshotStoragePath = null;
        }
        let performerId = performer?.id;
        let createdPerformer = null;
        try {
          if (isEdit) {
            await dispatch(
              updatePerformer({
                organizationId,
                performerId,
                values: payload,
              }),
            ).unwrap();
          } else {
            const result = await dispatch(
              createPerformer({ organizationId, values: payload }),
            ).unwrap();
            createdPerformer = result?.performer ?? null;
            performerId = createdPerformer?.id;
          }
          if (headshotFile && performerId) {
            await dispatch(
              uploadPerformerHeadshot({
                organizationId,
                performerId,
                file: headshotFile,
              }),
            );
          }
          const fetchResult = await dispatch(
            fetchPerformers({ organizationId }),
          ).unwrap();
          setHeadshotFile(null);
          setClearHeadshot(false);
          if (createdPerformer) {
            const updated =
              fetchResult?.performers?.[createdPerformer.id] ?? createdPerformer;
            if (values.sendEditLinkAfterCreate && updated?.id) {
              const email = (updated.email || "").trim();
              if (email) {
                try {
                  await dispatch(
                    sendPerformerEditLinkEmail({
                      organizationId,
                      performerId: updated.id,
                    }),
                  ).unwrap();
                  onMessage?.("Edit link sent to " + (updated.email || "performer"));
                } catch (err) {
                  onMessage?.(err?.message || "Failed to send edit link email");
                }
              } else {
                onMessage?.("Add an email to this performer to send the edit link.");
              }
            }
            onSuccess?.(updated);
          } else {
            onSuccess?.();
          }
        } catch (err) {
          const message = err?.message || "Save failed";
          const isEmailConflict =
            (err?.code && String(err.code).includes("already-exists")) ||
            message.includes("email");
          if (isEmailConflict) {
            setFieldError("email", message);
          } else {
            setFieldError("name", message);
          }
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
        <>
          <LoadingOverlay show={isSubmitting} />
          <Form>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              name="name"
              label="Name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              fullWidth
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(errors.email)}
              helperText={errors.email || "Optional. Required only for sending edit link by email."}
              fullWidth
            />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Headshot
            </Typography>
            <Dropzone
              onDrop={(acceptedFiles) => {
                if (acceptedFiles.length > 0) {
                  setPendingHeadshotFile(acceptedFiles[0]);
                  setClearHeadshot(false);
                }
              }}
              accept={{ "image/*": [] }}
              maxFiles={1}
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                const previewSrc =
                  headshotPreviewUrl ||
                  (performer?.headshotUrl && !headshotFile && !clearHeadshot
                    ? performer.headshotUrl
                    : null);
                return (
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "2px dashed",
                      borderColor: isDragActive ? "primary.main" : "grey.400",
                      borderRadius: 1,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: 140,
                      cursor: "pointer",
                      backgroundColor: isDragActive ? "action.hover" : "grey.50",
                    }}
                  >
                    <input {...getInputProps()} />
                    {previewSrc ? (
                      <>
                        <Box
                          component="img"
                          src={previewSrc}
                          alt="Headshot"
                          sx={{
                            width: 100,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {headshotFile ? "Drop a new image or click to change" : "Drop a new image or click to replace"}
                        </Typography>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setHeadshotFile(null);
                            setClearHeadshot(true);
                          }}
                        >
                          Clear headshot
                        </Button>
                      </>
                    ) : (
                      <>
                        <CloudUploadIcon color="action" sx={{ fontSize: 40 }} />
                        <Typography variant="body2" color="text.secondary">
                          Drop headshot or click to select
                        </Typography>
                      </>
                    )}
                  </Box>
                );
              }}
            </Dropzone>
            <HeadshotCropModal
              open={Boolean(pendingHeadshotFile)}
              imageFile={pendingHeadshotFile}
              onApply={(file) => {
                setHeadshotFile(file);
                setPendingHeadshotFile(null);
              }}
              onCancel={() => setPendingHeadshotFile(null)}
            />
            <TextField
              name="bio"
              label="Bio"
              value={values.bio}
              onChange={handleChange}
              onBlur={handleBlur}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              name="credits"
              label="Credits"
              value={values.credits}
              onChange={handleChange}
              onBlur={handleBlur}
              multiline
              rows={2}
              fullWidth
            />
            <Typography variant="subtitle2" color="text.secondary">
              Links
            </Typography>
            <FieldArray name="links">
              {({ push, remove }) => (
                <Box display="flex" flexDirection="column" gap={2}>
                  {(values.links || []).map((link, index) => {
                    const preset = link.presetId
                      ? LINK_PRESETS.find((p) => p.id === link.presetId)
                      : null;
                    const isPending = !link.presetId;
                    return (
                      <Box
                        key={`link-${index}`}
                        display="flex"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="flex-start"
                      >
                        {isPending ? (
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" width="100%">
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                              <InputLabel id={`link-type-${index}`}>Link type</InputLabel>
                              <Select
                                labelId={`link-type-${index}`}
                                value=""
                                label="Link type"
                                onChange={(e) =>
                                  setFieldValue(`links.${index}.presetId`, e.target.value)
                                }
                                renderValue={() => "Choose link type"}
                              >
                                {LINK_PRESETS.map((p) => {
                                  const Logo = p.Logo;
                                  return (
                                    <MenuItem key={p.id} value={p.id}>
                                      <Box display="flex" alignItems="center" gap={1.5}>
                                        {Logo && <Logo sx={{ fontSize: 22 }} />}
                                        <span>{p.label}</span>
                                      </Box>
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              onClick={() => remove(index)}
                              aria-label="Remove link"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ height: 40, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                              {preset?.Logo && (() => {
                                const Logo = preset.Logo;
                                return (
                                  <Logo sx={{ fontSize: 24, color: "text.secondary" }} />
                                );
                              })()}
                              <Typography variant="body2" sx={{ minWidth: 80 }}>
                                {preset?.label}
                              </Typography>
                            </Box>
                            {preset?.inputType === "username" && (
                              <TextField
                                size="small"
                                name={`links.${index}.value`}
                                placeholder={preset.placeholder}
                                value={link.value}
                                onChange={(e) =>
                                  setFieldValue(`links.${index}.value`, e.target.value)
                                }
                                onBlur={handleBlur}
                                error={Boolean(errors.links?.[index])}
                                helperText={errors.links?.[index]}
                                sx={{ flex: 1, minWidth: 160 }}
                              />
                            )}
                            {preset?.inputType === "url" && (
                              <TextField
                                size="small"
                                name={`links.${index}.value`}
                                placeholder={preset.placeholder}
                                value={link.value}
                                onChange={(e) =>
                                  setFieldValue(`links.${index}.value`, e.target.value)
                                }
                                onBlur={handleBlur}
                                error={Boolean(errors.links?.[index])}
                                helperText={errors.links?.[index]}
                                sx={{ flex: 1, minWidth: 200 }}
                              />
                            )}
                            {preset?.inputType === "other" && (
                              <Box display="flex" alignItems="flex-start" gap={1} flex={1} minWidth={0}>
                                <TextField
                                  size="small"
                                  placeholder="Label"
                                  value={link.customLabel}
                                  onChange={(e) =>
                                    setFieldValue(`links.${index}.customLabel`, e.target.value)
                                  }
                                  sx={{ minWidth: 120 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                  <TextField
                                    size="small"
                                    name={`links.${index}.value`}
                                    placeholder="https://..."
                                    value={link.value}
                                    onChange={(e) =>
                                      setFieldValue(`links.${index}.value`, e.target.value)
                                    }
                                    onBlur={handleBlur}
                                    error={Boolean(errors.links?.[index])}
                                    helperText={errors.links?.[index]}
                                    fullWidth
                                  />
                                </Box>
                              </Box>
                            )}
                            <Box sx={{ height: 40, display: "flex", alignItems: "center", flexShrink: 0 }}>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                                aria-label="Remove link"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </>
                        )}
                      </Box>
                    );
                  })}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => push({ presetId: "", value: "", customLabel: "" })}
                  >
                    Add link
                  </Button>
                </Box>
              )}
            </FieldArray>
          </Box>
          {!isEdit && (
            <FormControlLabel
              control={
                <Checkbox
                  name="sendEditLinkAfterCreate"
                  checked={Boolean(values.sendEditLinkAfterCreate)}
                  onChange={(e) => setFieldValue("sendEditLinkAfterCreate", e.target.checked)}
                  onBlur={handleBlur}
                />
              }
              label="Send edit link to performer"
              sx={{ mt: 1 }}
            />
          )}
          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={onCancel}>{cancelLabel}</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {resolvedSubmitLabel}
            </Button>
          </Box>
        </Form>
        </>
      )}
    </Formik>
  );
};

export default PerformerForm;
